import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube,
    FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaArrowRight, FaWhatsapp, FaTiktok, FaBuilding
} from 'react-icons/fa';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/helpers';

const Footer = () => {
    const { settings, loading } = useSettings();

    if (loading || !settings) return null;

    const { general, footer, socialMedia, contactPage } = settings;
    const currentYear = new Date().getFullYear();

    const socialIcons = [
        { key: 'facebook', icon: FaFacebookF, color: 'hover:bg-blue-600', label: 'Facebook' },
        { key: 'instagram', icon: FaInstagram, color: 'hover:bg-pink-600', label: 'Instagram' },
        { key: 'twitter', icon: FaTwitter, color: 'hover:bg-sky-500', label: 'Twitter' },
        { key: 'linkedin', icon: FaLinkedinIn, color: 'hover:bg-blue-700', label: 'LinkedIn' },
        { key: 'youtube', icon: FaYoutube, color: 'hover:bg-red-600', label: 'YouTube' },
        { key: 'tiktok', icon: FaTiktok, color: 'hover:bg-black', label: 'TikTok' }
    ].filter(social => socialMedia?.[social.key]);

    const logoWhiteSrc = general?.logoWhite ? getImageUrl(general.logoWhite) : null;
    const logoSrc = general?.logo ? getImageUrl(general.logo) : null;

    const extraBranches = contactPage?.branches || [];

    return (
        // SEO: footer landmark is already correct. Added aria-label for screen readers
        <footer className="bg-gray-900 text-gray-300" aria-label="Site footer">
            <div className="container mx-auto px-4 pt-10 pb-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                    {/* 1. Company Info */}
                    <div>
                        <div className="flex items-center space-x-3 mb-4">
                            {logoWhiteSrc ? (
                                <img src={logoWhiteSrc} alt={`${general?.siteName || 'Wise International Consultancy'} logo`} className="h-10 w-auto" width="40" height="40"
                                    onError={(e) => { e.target.style.display = 'none'; }} loading="lazy" />
                            ) : logoSrc ? (
                                <img src={logoSrc} alt={`${general?.siteName || 'Wise International Consultancy'} logo`} className="h-10 w-auto brightness-0 invert" width="40" height="40"
                                    onError={(e) => { e.target.style.display = 'none'; }} loading="lazy" />
                            ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center" aria-hidden="true">
                                    <span className="text-white font-bold text-lg">{general?.siteName?.charAt(0) || 'N'}</span>
                                </div>
                            )}
                            <div>
                                {/* SEO: Using span instead of h3 in footer — site name is not a heading */}
                                <span className="text-lg font-bold text-white leading-tight block">{general?.siteName || 'Wise International Consultancy'}</span>
                                {general?.tagline && <p className="text-[10px] text-gray-500 leading-tight">{general.tagline}</p>}
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs leading-relaxed mb-4">
                            {footer?.description || general?.description || 'Your trusted education consultancy.'}
                        </p>

                        {/* SEO: Added aria-labels to social links for accessibility */}
                        <div className="flex flex-wrap gap-1.5" role="list" aria-label="Social media links">
                            {socialIcons.map((social, index) => (
                                <a key={index} href={socialMedia[social.key]} target="_blank" rel="noopener noreferrer"
                                    aria-label={`Follow us on ${social.label}`}
                                    role="listitem"
                                    className={`w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center transition-all ${social.color} hover:-translate-y-0.5`}>
                                    <social.icon className="w-3.5 h-3.5 text-white" aria-hidden="true" />
                                </a>
                            ))}
                            {general?.whatsapp && (
                                <a href={`https://wa.me/${general.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                                    aria-label="Contact us on WhatsApp"
                                    role="listitem"
                                    className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center transition-all hover:bg-green-600 hover:-translate-y-0.5">
                                    <FaWhatsapp className="w-3.5 h-3.5 text-white" aria-hidden="true" />
                                </a>
                            )}
                        </div>
                    </div>

                    {/* 2. Quick Links */}
                    {/* SEO FIX: h4 → h2 for proper heading hierarchy in footer landmark */}
                    <nav aria-label="Quick links">
                        <h2 className="text-base font-semibold text-white mb-3">Quick Links</h2>
                        <ul className="space-y-2 text-sm">
                            {footer?.quickLinks?.length > 0 ? (
                                footer.quickLinks.map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} className="flex items-center text-gray-400 hover:text-secondary-400 group transition">
                                            <FaArrowRight className="w-2.5 h-2.5 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                                            {link.name}
                                        </Link>
                                    </li>
                                ))
                            ) : (
                                [
                                    { name: 'Home', path: '/' },
                                    { name: 'About Us', path: '/about' },
                                    { name: 'Services', path: '/services' },
                                    { name: 'Universities', path: '/universities' },
                                    { name: 'Courses', path: '/courses' },
                                ].map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} className="flex items-center text-gray-400 hover:text-secondary-400 group transition">
                                            <FaArrowRight className="w-2.5 h-2.5 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                                            {link.name}
                                        </Link>
                                    </li>
                                ))
                            )}
                        </ul>
                    </nav>

                    {/* 3. Study Destinations */}
                    <nav aria-label="Study destinations">
                        <h2 className="text-base font-semibold text-white mb-3">Study Destinations</h2>
                        <ul className="space-y-2 text-sm">
                            {footer?.studyDestinations?.length > 0 ? (
                                footer.studyDestinations.map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} className="flex items-center text-gray-400 hover:text-secondary-400 group transition">
                                            <FaArrowRight className="w-2.5 h-2.5 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                                            {link.name}
                                        </Link>
                                    </li>
                                ))
                            ) : (
                                [
                                    { name: 'Study in India', path: '/study-in/india' },
                                    { name: 'Study in Nepal', path: '/study-in/nepal' },
                                    { name: 'Study in Bangladesh', path: '/study-in/bangladesh' },
                                ].map((link, index) => (
                                    <li key={index}>
                                        <Link to={link.path} className="flex items-center text-gray-400 hover:text-secondary-400 group transition">
                                            <FaArrowRight className="w-2.5 h-2.5 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                                            {link.name}
                                        </Link>
                                    </li>
                                ))
                            )}
                        </ul>
                    </nav>

                    {/* 4. Contact Info (Head Office) */}
                    <div>
                        <h2 className="text-base font-semibold text-white mb-3">Head Office</h2>
                        {/* SEO: address element for structured contact information */}
                        <address className="space-y-2.5 text-xs text-gray-400 not-italic">
                            {general?.address?.line1 && (
                                <div className="flex items-start">
                                    <FaMapMarkerAlt className="w-3.5 h-3.5 text-secondary-500 mt-0.5 mr-2.5 flex-shrink-0" aria-hidden="true" />
                                    <span>
                                        {general.address.line1}
                                        {general.address.line2 && <><br />{general.address.line2}</>}
                                        <br />{general.address.city}, {general.address.country}
                                    </span>
                                </div>
                            )}
                            {general?.phone && (
                                <div className="flex items-start">
                                    <FaPhone className="w-3.5 h-3.5 text-secondary-500 mt-0.5 mr-2.5 flex-shrink-0" aria-hidden="true" />
                                    <div>
                                        <a href={`tel:${general.phone}`} className="hover:text-secondary-400 block" aria-label={`Call us at ${general.phone}`}>{general.phone}</a>
                                        {general.alternatePhone && (
                                            <a href={`tel:${general.alternatePhone}`} className="hover:text-secondary-400 block mt-0.5" aria-label={`Alternative number ${general.alternatePhone}`}>{general.alternatePhone}</a>
                                        )}
                                    </div>
                                </div>
                            )}
                            {general?.email && (
                                <div className="flex items-center">
                                    <FaEnvelope className="w-3.5 h-3.5 text-secondary-500 mr-2.5 flex-shrink-0" aria-hidden="true" />
                                    <a href={`mailto:${general.email}`} className="hover:text-secondary-400 truncate block" aria-label={`Email us at ${general.email}`}>{general.email}</a>
                                </div>
                            )}
                            {general?.officeHours?.weekdays && (
                                <div className="flex items-start">
                                    <FaClock className="w-3.5 h-3.5 text-secondary-500 mt-0.5 mr-2.5 flex-shrink-0" aria-hidden="true" />
                                    <div>
                                        <span className="block">{general.officeHours.weekdays}</span>
                                        {general.officeHours.saturday && <span className="block mt-0.5">{general.officeHours.saturday}</span>}
                                    </div>
                                </div>
                            )}
                        </address>

                        {/* CTA Buttons */}
                        <div className="mt-4 flex gap-2">
                            <Link to="/apply" className="flex-1 text-center py-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-semibold rounded text-xs hover:shadow-lg transition">
                                Apply
                            </Link>
                            <Link to="/book-appointment" className="flex-1 text-center py-2 bg-white/5 text-white font-semibold rounded text-xs border border-white/10 hover:bg-white/10 transition">
                                Consult
                            </Link>
                        </div>
                    </div>
                </div>

                {/* ================= EXTRA BRANCHES SECTION ================= */}
                {extraBranches.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-800">
                        <h2 className="text-sm font-semibold text-white mb-4 flex items-center">
                            <FaBuilding className="mr-2 text-secondary-500" aria-hidden="true" />
                            Other Branches
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {extraBranches.map((branch, index) => (
                                <address key={index} className="bg-gray-800/30 border border-gray-800 rounded-xl p-3 hover:border-gray-700 transition-colors not-italic">
                                    <h3 className="text-white text-xs font-semibold mb-2 truncate">{branch.name}</h3>
                                    <ul className="space-y-1.5 text-[11px]">
                                        {branch.address && (
                                            <li className="flex items-start text-gray-400">
                                                <FaMapMarkerAlt className="w-3 h-3 text-gray-500 mt-0.5 mr-2 flex-shrink-0" aria-hidden="true" />
                                                <span className="truncate">{branch.address}</span>
                                            </li>
                                        )}
                                        {branch.phone && (
                                            <li className="flex items-center text-gray-400">
                                                <FaPhone className="w-3 h-3 text-gray-500 mr-2 flex-shrink-0" aria-hidden="true" />
                                                <a href={`tel:${branch.phone}`} className="hover:text-secondary-400 transition truncate" aria-label={`Call ${branch.name} at ${branch.phone}`}>{branch.phone}</a>
                                            </li>
                                        )}
                                        {branch.email && (
                                            <li className="flex items-center text-gray-400">
                                                <FaEnvelope className="w-3 h-3 text-gray-500 mr-2 flex-shrink-0" aria-hidden="true" />
                                                <a href={`mailto:${branch.email}`} className="hover:text-secondary-400 transition truncate" aria-label={`Email ${branch.name}`}>{branch.email}</a>
                                            </li>
                                        )}
                                    </ul>
                                </address>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ================= BOTTOM COPYRIGHT BAR ================= */}
            <div className="border-t border-gray-800 bg-gray-900/80">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-2">
                        <p className="text-gray-500 text-[11px]">
                            {footer?.copyrightText || `© ${currentYear} ${general?.siteName || 'Wise International Consultancy'}. All rights reserved.`}
                        </p>
                        {/* SEO: Add links to legal pages for trust signals */}
                        <div className="flex items-center gap-3 text-[11px]">
                            <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-300 transition">Privacy Policy</Link>
                            <span className="text-gray-700">|</span>
                            <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition">Terms</Link>
                            {footer?.showDeveloperCredit && footer?.developerCredit && (
                                <>
                                    <span className="text-gray-700">|</span>
                                    <p className="text-white-600 text-[10px]">{footer.developerCredit}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;